import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  useAggiornaStati,
  useFermaAggiornaStati,
  useLanciaAggiornaStati,
} from '../api/integrazioni.js';
import { dataOra } from '../formati.js';
import { t } from '../i18n/lingua.js';

/**
 * «Aggiorna gli stati in Business Central», da una data.
 *
 * Nel v2 è la voce «Aggiorna gli status degli ordini di acquisto di Business
 * Central» di Dati Business Central: chiede una data, e rimanda a BC ogni
 * ordine da lì in poi. Qui il numero degli ordini si vede **prima** di
 * partire, perché ognuno è una decina di chiamate attraverso l'agente e un
 * mese di ordini sono ore; e il lavoro gira in sottofondo, a pagina chiusa,
 * con il suo posto nelle «Ultime esecuzioni».
 */
export function AggiornaStatiBc() {
  const [dal, setDal] = useState('');
  const [conferma, setConferma] = useState(false);
  const stato = useAggiornaStati(dal);
  const lancia = useLanciaAggiornaStati();
  const ferma = useFermaAggiornaStati();

  const s = stato.data;
  const lavoro = s?.running ?? null;
  const errore =
    lancia.error instanceof ErroreApi
      ? lancia.error.message
      : stato.error instanceof ErroreApi
        ? stato.error.message
        : null;

  const parti = () =>
    lancia.mutate(dal, {
      onSuccess: () => setConferma(false),
      onError: () => setConferma(false),
    });

  return (
    <div className="card" style={{ margin: '14px 20px' }}>
      <div className="card-head">
        <span className="card-title">{t('Aggiorna gli stati in Business Central')}</span>
        {s?.mode === 'prova' && (
          <span
            className="badge badge-warn"
            style={{ marginLeft: 8 }}
            title={t('Fino al passaggio partono solo gli ordini dei clienti di prova, con il numero spostato.')}
          >
            {t('in prova')}
          </span>
        )}
      </div>

      <div className="card-body">
        <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
          {t('Rimanda a Business Central stato, date, nave, container e DAU degli ordini della SPA, dalla data dell’ordine scelta in poi. Serve a rimetterlo in pari quando il CRM è andato avanti da solo. Ogni ordine sono una decina di chiamate: un mese di ordini richiede qualche ora, e il lavoro continua anche a pagina chiusa.')}
        </p>

        {lavoro ? (
          <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <progress max={lavoro.total} value={lavoro.done} style={{ width: 220 }} />
            <span className="num">
              {t('{fatti} ordini su {totale}', { fatti: lavoro.done, totale: lavoro.total })}
            </span>
            <span className="dim">
              {t('{ok} accettati, {problemi} con problemi', {
                ok: lavoro.ok,
                problemi: lavoro.problems,
              })}
            </span>
            <span className="dim num" style={{ fontSize: 12 }}>
              {t('dal {data}, partito {quando}', { data: lavoro.from, quando: dataOra(lavoro.startedAt) })}
            </span>
            <button
              className="btn"
              style={{ marginLeft: 'auto' }}
              disabled={ferma.isPending}
              onClick={() => ferma.mutate()}
            >
              {ferma.data?.stopping ? t('Si ferma dopo questo ordine…') : t('Ferma')}
            </button>
          </div>
        ) : s?.mode === 'spento' ? (
          <div className="nota-prezzo">
            {t('Su questo server l’invio a Business Central è spento: non partirebbe niente.')}
          </div>
        ) : s && !s.spa ? (
          <div className="nota-prezzo">
            {t('Si aggiornano solo gli ordini della SPA: gli stati della Inc in Business Central li porta avanti chi ci lavora.')}
          </div>
        ) : (
          <div className="row" style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ minWidth: 180 }}>
              <label htmlFor="aggiorna-stati-dal">{t('Ordini con data dal')}</label>
              <input
                id="aggiorna-stati-dal"
                className="input"
                type="date"
                value={dal}
                onChange={(e) => {
                  setDal(e.target.value);
                  setConferma(false);
                }}
              />
            </div>
            {dal && s?.orders !== null && s?.orders !== undefined && (
              <span className="dim" style={{ paddingBottom: 8 }}>
                {s.orders === 0
                  ? t('Nessun ordine da mandare.')
                  : t('Partirebbero {n} ordini.', { n: s.orders.toLocaleString('it-IT') })}
              </span>
            )}
            {!conferma ? (
              <button
                className="btn btn-primary"
                disabled={!dal || !s?.orders || lancia.isPending}
                onClick={() => setConferma(true)}
              >
                {t('Aggiorna')}
              </button>
            ) : (
              <span className="row" style={{ gap: 6 }}>
                <button className="btn btn-ghost" onClick={() => setConferma(false)}>
                  {t('Lascia stare')}
                </button>
                <button className="btn btn-primary" disabled={lancia.isPending} onClick={parti}>
                  {t('Sì, manda {n} ordini', { n: s?.orders ?? 0 })}
                </button>
              </span>
            )}
          </div>
        )}

        {errore && (
          <div className="avviso" role="alert" style={{ marginTop: 12 }}>
            <span className="avviso-segno">▲</span>
            <span>{errore}</span>
          </div>
        )}
      </div>
    </div>
  );
}
